// The groove engine for the /home-key course — Units 7–8. Owns everything
// rhythmic: the 16-step grid and its named starter patterns, swing, sample-free
// drum synthesis (kick / snare / hat shaped from oscillators and noise, the
// same approach as keyboardPlan.js's jam room, but self-contained here), the
// style lenses, a lookahead transport, and the full-track renderer that layers
// a learner's keepers into one playing song.
//
// The grid logic is pure and unit-tested (grooveStepEvents especially); the
// audio side routes every voice through homeKeyAudio's master bus and is
// guarded so importing and calling everything in jsdom (no AudioContext)
// never throws — handles degrade to no-ops, mirroring homeKeyAudio.

import {
  chordMidis,
  MINOR_TRIADS,
  RAISED_V,
  chordPhrase,
} from "./homeKeyTheory";
import {
  getCtx,
  getMasterBus,
  playPhrase,
  startDrone,
} from "./homeKeyAudio";

/* ============================================================
   The grid — 16 sixteenth-note steps per bar, 4 beats.
   Strong floors on steps 0 and 8 (beats 1 and 3); the backbeat
   answers on 4 and 12 (beats 2 and 4).
   ============================================================ */

export const STEPS_PER_BAR = 16;

// Named starter patterns. Model: { kick, snare, hat, hatOpen? } — arrays of
// step indices 0..15. These are starting points the learner edits, not laws.
export const DRUM_PATTERNS = {
  // Just the floors — beats 1 and 3. The simplest true thing.
  heartbeat: { kick: [0, 8], snare: [], hat: [] },
  // The classic: kick on the floors, snare answering on 2 and 4, hats
  // carrying energy on every eighth.
  backbeat: {
    kick: [0, 8],
    snare: [4, 12],
    hat: [0, 2, 4, 6, 8, 10, 12, 14],
  },
  // A lazy backbeat: the kick limps back in late after beat 3, the hats
  // thin out to quarters. Swing does the rest of the slouching.
  lofi: { kick: [0, 8, 10], snare: [4, 12], hat: [0, 4, 8, 12] },
  // Four on the floor, open hats breathing on every offbeat.
  synthwave: {
    kick: [0, 4, 8, 12],
    snare: [4, 12],
    hat: [],
    hatOpen: [2, 6, 10, 14],
  },
  // Driving eighth hats with a kick that pushes ahead of the floors.
  indie: {
    kick: [0, 6, 8, 14],
    snare: [4, 12],
    hat: [0, 2, 4, 6, 8, 10, 12, 14],
  },
};

// A pattern argument may be a DRUM_PATTERNS key or a pattern object (the drum
// builder edits an object live — never copy it, so edits land mid-loop).
// Anything else resolves to null: no drums.
const resolvePattern = (pattern) => {
  if (pattern && typeof pattern === "object") return pattern;
  return DRUM_PATTERNS[pattern] || null;
};

// How late an odd sixteenth lands, in ms. Swing 0.5 = straight (no delay);
// higher drags every offbeat sixteenth toward the next step. Even steps are
// never moved — swing bends the weak, not the strong.
export const swingDelay = (step, swing = 0.5, stepMs = 0) => {
  if (!Number.isFinite(step) || !Number.isFinite(swing) || !Number.isFinite(stepMs)) {
    return 0;
  }
  const odd = ((step % 2) + 2) % 2 === 1;
  return odd ? (swing - 0.5) * 2 * stepMs * 0.6 : 0;
};

/* ============================================================
   grooveStepEvents — the pure heart of the transport. Describes
   everything that sounds on one step, no audio involved.
   ============================================================ */

// What sounds on `step` of a looping groove, as an array of event descriptors:
//   { type: 'kick' | 'snare' | 'hat' | 'hatOpen' | 'bass' | 'chord',
//     midi?, midis?, dur? }
// `dur` is in *steps* (sixteenths) — the transport converts to ms at its bpm,
// which keeps this function pure of tempo.
//
// Options:
// - pattern: DRUM_PATTERNS key or pattern object (null/absent = no drums).
// - bassPlan: array of bass midi notes, one per bar, cycled by its own
//   length. The bass locks to the kick: it fires wherever the kick fires
//   (falling back to the heartbeat floors 0/8 when there's no pattern),
//   sounding that bar's note — roots follow chords, bass follows kick.
// - chordDegrees: array of chord degrees, one per bar, cycled by its own
//   length; a 'chord' event fires at each bar's step 0 holding the whole bar.
// - mode: 'major' (default) | 'minor' — minor indexes MINOR_TRIADS and
//   renders the borrowed-doorbell token '5+' as RAISED_V.
export const grooveStepEvents = (step, opts = {}) => {
  const { pattern, bassPlan, chordDegrees, mode } = opts;
  const pat = resolvePattern(pattern);
  const pos = ((step % STEPS_PER_BAR) + STEPS_PER_BAR) % STEPS_PER_BAR;
  const bar = Math.floor(step / STEPS_PER_BAR);
  const events = [];

  const kicks = (pat && pat.kick && pat.kick.length ? pat.kick : [0, 8]);
  if (pat) {
    if ((pat.kick || []).includes(pos)) events.push({ type: "kick" });
    if ((pat.snare || []).includes(pos)) events.push({ type: "snare" });
    if ((pat.hat || []).includes(pos)) events.push({ type: "hat" });
    if ((pat.hatOpen || []).includes(pos)) events.push({ type: "hatOpen" });
  }

  if (Array.isArray(bassPlan) && bassPlan.length && kicks.includes(pos)) {
    const len = bassPlan.length;
    const midi = bassPlan[((bar % len) + len) % len];
    if (midi != null) events.push({ type: "bass", midi, dur: 2 });
  }

  if (Array.isArray(chordDegrees) && chordDegrees.length && pos === 0) {
    const len = chordDegrees.length;
    const degree = chordDegrees[((bar % len) + len) % len];
    const midis =
      mode === "minor"
        ? degree === "5+"
          ? RAISED_V.slice()
          : (MINOR_TRIADS[degree] || []).slice()
        : chordMidis(degree);
    if (midis.length) {
      events.push({ type: "chord", midis, dur: STEPS_PER_BAR });
    }
  }

  return events;
};

/* ============================================================
   Drum synthesis — sample-free, every hit shaped from oscillators
   and noise (keyboardPlan.js's proven recipes), routed through
   homeKeyAudio's master bus. `whenSec` is seconds from now.
   ============================================================ */

// A reusable white-noise buffer (built lazily, once) for snare / hat.
let noiseBuf = null;
const getNoise = (ctx) => {
  if (noiseBuf) return noiseBuf;
  const buf = ctx.createBuffer(1, ctx.sampleRate * 0.4, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuf = buf;
  return buf;
};

// Kick: a sine dropping 150→50Hz, plus a tiny noise click for the beater.
export const playKick = (whenSec = 0) => {
  const ctx = getCtx();
  const bus = getMasterBus();
  if (!ctx || !bus) return;
  const t0 = ctx.currentTime + Math.max(0, whenSec);
  const g = ctx.createGain();
  g.connect(bus);
  g.gain.setValueAtTime(0.9, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.17);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, t0);
  osc.frequency.exponentialRampToValueAtTime(50, t0 + 0.12);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + 0.2);
  // the click: a whisper of highpassed noise right at the attack
  const click = ctx.createBufferSource();
  click.buffer = getNoise(ctx);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 1200;
  const cg = ctx.createGain();
  cg.gain.setValueAtTime(0.25, t0);
  cg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.02);
  click.connect(hp);
  hp.connect(cg);
  cg.connect(bus);
  click.start(t0);
  click.stop(t0 + 0.03);
};

// Snare: a highpassed noise burst plus a low tonal body around 190Hz.
export const playSnare = (whenSec = 0) => {
  const ctx = getCtx();
  const bus = getMasterBus();
  if (!ctx || !bus) return;
  const t0 = ctx.currentTime + Math.max(0, whenSec);
  const noise = ctx.createBufferSource();
  noise.buffer = getNoise(ctx);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 1800;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.5, t0);
  ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
  noise.connect(hp);
  hp.connect(ng);
  ng.connect(bus);
  noise.start(t0);
  noise.stop(t0 + 0.16);
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.value = 190;
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.25, t0);
  og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
  osc.connect(og);
  og.connect(bus);
  osc.start(t0);
  osc.stop(t0 + 0.1);
};

// Hat: a short burst of noise highpassed ~7kHz; open hats ring longer.
export const playHat = (whenSec = 0, open = false) => {
  const ctx = getCtx();
  const bus = getMasterBus();
  if (!ctx || !bus) return;
  const t0 = ctx.currentTime + Math.max(0, whenSec);
  const noise = ctx.createBufferSource();
  noise.buffer = getNoise(ctx);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 7000;
  const g = ctx.createGain();
  const dur = open ? 0.2 : 0.045;
  g.gain.setValueAtTime(open ? 0.14 : 0.18, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  noise.connect(hp);
  hp.connect(g);
  g.connect(bus);
  noise.start(t0);
  noise.stop(t0 + dur + 0.02);
};

/* ============================================================
   Style lenses — the same notes wearing different clothes.
   Patch variants are plain objects conforming to homeKeyAudio's
   patch shape (oscs / filter / env / peak); the lensless bass
   voice bassDrop lives here too — it's the patch the bass lesson
   records with (played an octave down by the components layer).
   ============================================================ */

export const GROOVE_PATCHES = {
  // The lo-fi keys: softKeys with the filter half-closed and a longer sag.
  lofiKeys: {
    id: "lofiKeys",
    oscs: [
      { type: "triangle", detune: -4, gain: 0.8 },
      { type: "sine", octave: 1, gain: 0.05 },
    ],
    filter: { frequency: 1100, Q: 0.5 },
    env: { attack: 0.012, decay: 0.4, sustain: 0.5, release: 0.6 },
    peak: 0.18,
  },
  // The synthwave keys: saw-lean and bright, a sub octave underneath.
  synthwaveKeys: {
    id: "synthwaveKeys",
    oscs: [
      { type: "sawtooth", detune: -7, gain: 0.5 },
      { type: "sawtooth", detune: 7, gain: 0.5 },
      { type: "triangle", octave: -1, gain: 0.25 },
    ],
    filter: { frequency: 3200, Q: 0.8 },
    env: { attack: 0.01, decay: 0.3, sustain: 0.6, release: 0.4 },
    peak: 0.16,
  },
  // The indie keys: clean, quick, a hint of saw edge.
  indieKeys: {
    id: "indieKeys",
    oscs: [
      { type: "triangle", detune: 0, gain: 0.9 },
      { type: "sawtooth", detune: 3, gain: 0.12 },
    ],
    filter: { frequency: 3000, Q: 0.6 },
    env: { attack: 0.005, decay: 0.22, sustain: 0.5, release: 0.35 },
    peak: 0.2,
  },
  // The bass voice: round sine-forward low end, quick and dry. Used for
  // recording basslines (an octave down via the voices hook) and by the
  // track mix, which drops stored bass phrases the octave itself.
  bassDrop: {
    id: "bassDrop",
    oscs: [
      { type: "sine", detune: 0, gain: 0.85 },
      { type: "triangle", detune: 0, gain: 0.35 },
    ],
    filter: { frequency: 700, Q: 0.7 },
    env: { attack: 0.008, decay: 0.25, sustain: 0.7, release: 0.25 },
    peak: 0.24,
  },
};

// The lenses: { id, label, blurb, bpm, swing, patternKey, patch }. `patch`
// may be a patch object (from GROOVE_PATCHES) or a homeKeyAudio patch name;
// `patternKey` null means the lens wears no drums.
export const STYLE_LENSES = {
  dreamPop: {
    id: "dreamPop",
    label: "dream pop",
    blurb: "Haze and glow — no drums, just the pad breathing.",
    bpm: 84,
    swing: 0.5,
    patternKey: null,
    patch: "dreamPad",
  },
  lofi: {
    id: "lofi",
    label: "lo-fi",
    blurb: "Dusty and unhurried — hats swung lazy, filter half-closed.",
    bpm: 74,
    swing: 0.56,
    patternKey: "lofi",
    patch: GROOVE_PATCHES.lofiKeys,
  },
  synthwave: {
    id: "synthwave",
    label: "synthwave",
    blurb: "Neon on the highway — four on the floor, hats open on the off.",
    bpm: 96,
    swing: 0.5,
    patternKey: "synthwave",
    patch: GROOVE_PATCHES.synthwaveKeys,
  },
  indie: {
    id: "indie",
    label: "indie",
    blurb: "Bright room, live band — driving eighths and clean keys.",
    bpm: 112,
    swing: 0.5,
    patternKey: "indie",
    patch: GROOVE_PATCHES.indieKeys,
  },
};

/* ============================================================
   Transport — the standard Web Audio lookahead pattern: a ~25ms
   timer schedules everything inside a 200ms horizon on the audio
   clock, so playback is sample-accurate while the JS stays lazy.
   ============================================================ */

const NOOP_GROOVE = { stop: () => {} };

// One step's audible events, scheduled `whenSec` from now (already
// swing-adjusted). Bass and chords go through playPhrase so their voices can
// be cut on stop; the returned handles are collected by the caller.
const scheduleStepAudio = (events, whenSec, stepMs, patches, handles) => {
  events.forEach((ev) => {
    if (ev.type === "kick") playKick(whenSec);
    else if (ev.type === "snare") playSnare(whenSec);
    else if (ev.type === "hat") playHat(whenSec, false);
    else if (ev.type === "hatOpen") playHat(whenSec, true);
    else if (ev.type === "bass") {
      handles.push(
        playPhrase(
          { notes: [{ midi: ev.midi, at: 0, dur: (ev.dur || 2) * stepMs }] },
          patches.bass,
          { delayMs: whenSec * 1000, gain: 0.9 }
        )
      );
    } else if (ev.type === "chord") {
      handles.push(
        playPhrase(
          {
            notes: ev.midis.map((midi) => ({
              midi,
              at: 0,
              dur: (ev.dur || STEPS_PER_BAR) * stepMs,
            })),
          },
          patches.chord,
          { delayMs: whenSec * 1000, gain: 0.8 }
        )
      );
    }
  });
};

// Start a looping groove. Returns { stop() }. Options:
// - pattern: DRUM_PATTERNS key or a live pattern object (edits are heard).
// - bpm / swing / bars: the loop is `bars` bars long (default 4).
// - onStep(step): fired (best-effort, via setTimeout near the audible
//   moment) with the loop step 0..bars*16-1 for UI highlighting — grid UIs
//   take step % STEPS_PER_BAR.
// - bassPlan / chordDegrees / mode: as grooveStepEvents.
// - patch / bassPatch: voices for chord / bass events (defaults: softKeys
//   by name, GROOVE_PATCHES.bassDrop).
// - startAtSec: absolute AudioContext time to start at (the track mix uses
//   this to line the drums up with its cycles); defaults to just ahead.
// jsdom-safe: with no AudioContext this is a silent no-op handle.
export const startGroove = ({
  pattern,
  bpm = 90,
  swing = 0.5,
  bars = 4,
  onStep,
  bassPlan,
  chordDegrees,
  mode,
  patch = "softKeys",
  bassPatch = GROOVE_PATCHES.bassDrop,
  startAtSec,
} = {}) => {
  const ctx = getCtx();
  if (!ctx) return NOOP_GROOVE;

  const stepSec = 60 / bpm / 4;
  const stepMs = stepSec * 1000;
  const totalSteps = Math.max(1, Math.floor(bars)) * STEPS_PER_BAR;
  const patches = { chord: patch, bass: bassPatch };
  const phraseHandles = [];
  const timeouts = new Set();
  let stopped = false;
  let step = 0;
  let nextTime =
    startAtSec != null ? startAtSec : ctx.currentTime + 0.1;

  // Best-effort UI hook: fire onStep near the audible moment, and never let
  // it take the transport down.
  const queueOnStep = (loopStep, delayMs) => {
    const timeout = setTimeout(() => {
      timeouts.delete(timeout);
      if (stopped) return;
      try {
        onStep(loopStep);
      } catch (e) {
        /* UI's problem, not the groove's */
      }
    }, delayMs);
    timeouts.add(timeout);
  };

  const schedule = () => {
    while (nextTime < ctx.currentTime + 0.2) {
      const loopStep = step % totalSteps;
      const when =
        nextTime - ctx.currentTime + swingDelay(loopStep, swing, stepMs) / 1000;
      const events = grooveStepEvents(loopStep, {
        pattern,
        bassPlan,
        chordDegrees,
        mode,
      });
      scheduleStepAudio(events, Math.max(0, when), stepMs, patches, phraseHandles);
      if (onStep) queueOnStep(loopStep, Math.max(0, when * 1000));
      nextTime += stepSec;
      step += 1;
    }
  };

  schedule();
  const timer = setInterval(schedule, 25);
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      phraseHandles.forEach((h) => h.stop());
    },
  };
};

/* ============================================================
   playTrackMix — the full-track renderer for the Studio and the
   capstone. One cycle = 4 bars at the lens's bpm; every enabled
   layer the learner has actually made gets scheduled into it,
   and the whole thing loops until stopped.
   ============================================================ */

// Options:
// - keepers: the progress state's keepers map ({ [keeperId]: payload }).
// - lensId: STYLE_LENSES key (dreamPop when unset/unknown).
// - layers: { drums, bass, chords, melody, drone } booleans — a layer plays
//   unless explicitly false (and unless its keeper is missing, which just
//   skips it).
// - section: 'A' (default — chordLoop + trackMelody, falling back to
//   homeCall) or 'B' (minorLoop + bSection, chords rendered in minor mode
//   so '5+' sounds the borrowed doorbell, drone on E).
// Chords render via chordPhrase(degrees, { chordMs: barMs, gapMs: 0, mode })
// so the loop's chords land exactly on the bars; melody and bass phrases are
// scheduled once per cycle from the cycle start, free-time over the grid —
// human-loose is fine and honest. Bass phrases are stored as played and
// dropped an octave here. Returns { stop, cycleMs }; jsdom-safe no-op handle.
export const playTrackMix = ({ keepers = {}, lensId, layers = {}, section = "A" } = {}) => {
  const lens = STYLE_LENSES[lensId] || STYLE_LENSES.dreamPop;
  const barMs = (60000 / lens.bpm) * 4;
  const cycleMs = barMs * 4;
  const minor = section === "B";
  const on = (name) => layers[name] !== false;

  const ctx = getCtx();
  if (!ctx) return { stop: () => {}, cycleMs };

  // Resolve each layer's source material; a missing keeper skips the layer.
  const chordKeeper = minor ? keepers.minorLoop : keepers.chordLoop;
  const degrees = (chordKeeper && chordKeeper.degrees) || null;
  const chordLoopPhrase = degrees
    ? chordPhrase(degrees, { chordMs: barMs, gapMs: 0, mode: minor ? "minor" : "major" })
    : (chordKeeper && chordKeeper.phrase) || null;
  const melodyKeeper = minor
    ? keepers.bSection
    : keepers.trackMelody || keepers.homeCall;
  const melodyPhrase = (melodyKeeper && melodyKeeper.phrase) || null;
  const bassSource = (keepers.bassline && keepers.bassline.phrase) || null;
  const bassPhrase =
    bassSource && bassSource.notes
      ? { notes: bassSource.notes.map((note) => ({ ...note, midi: note.midi - 12 })) }
      : null;
  const drumPattern =
    (keepers.beat && keepers.beat.pattern) ||
    (keepers.pulse && keepers.pulse.pattern) ||
    (lens.patternKey ? DRUM_PATTERNS[lens.patternKey] : null);

  const startSec = ctx.currentTime + 0.15;
  const handles = [];

  if (on("drone")) handles.push(startDrone(minor ? 40 : 43));
  if (on("drums") && drumPattern) {
    handles.push(
      startGroove({
        pattern: drumPattern,
        bpm: lens.bpm,
        swing: lens.swing,
        bars: 4,
        startAtSec: startSec,
      })
    );
  }

  // Cycle scheduler: shortly before each 4-bar cycle begins, schedule that
  // cycle's chord loop, melody, and bass from the cycle start.
  let cycle = 0;
  const scheduleCycles = () => {
    while (startSec + (cycle * cycleMs) / 1000 < ctx.currentTime + 0.3) {
      const delayMs = (startSec + (cycle * cycleMs) / 1000 - ctx.currentTime) * 1000;
      if (on("chords") && chordLoopPhrase) {
        handles.push(playPhrase(chordLoopPhrase, lens.patch, { delayMs, gain: 0.85 }));
      }
      if (on("melody") && melodyPhrase) {
        handles.push(playPhrase(melodyPhrase, lens.patch, { delayMs }));
      }
      if (on("bass") && bassPhrase) {
        handles.push(
          playPhrase(bassPhrase, GROOVE_PATCHES.bassDrop, { delayMs, gain: 0.9 })
        );
      }
      cycle += 1;
    }
  };
  scheduleCycles();
  const timer = setInterval(scheduleCycles, 100);

  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      handles.forEach((h) => h.stop());
    },
    cycleMs,
  };
};
